/**
 * @typedef {Parameters<import("@monaco-editor/react").OnMount>[0]} MonacoEditor - editor instance
 * @typedef {Parameters<Parameters<MonacoEditor["onKeyDown"]>[0]>[0]} IKeyboardEvent - Monaco keyboard event. Wraps the browser event.
 * @typedef {import("@monaco-editor/react").Monaco} Monaco - Monaco backend
 */

import { CHROME_VERSION, isChrome } from "@/constants/BrowserConstants";

/**
 * @callback InputFixCallback
 * @param {IKeyboardEvent} event
 * @returns {void}
 */

// todo: give these errors meaningful constant names (just so we know what they're tied to)
const ERROR_SUPPRESSION_LIST = [2304, 2552, 2570];
const C_KEY = 33;
const V_KEY = 52;
const X_KEY = 54;

const NEEDS_FIXES = isChrome && CHROME_VERSION > 133;

/**
 * Prevents default and propagation
 * @param {IKeyboardEvent} event
 */
const consumeEvent = (event) => {
  event.browserEvent.stopPropagation();
  event.browserEvent.preventDefault();
};

/**
 * Copies selected text to clipboard.
 * @param {MonacoEditor} editor
 */
const copyToClipboard = (editor) => {
  const selection = editor.getSelection();
  const value = editor.getModel().getValueInRange(selection);
  navigator.clipboard.writeText(value);
};

/**
 * Reads from clipboard on "ctrl v" paste and inserts at selection.
 * @param {MonacoEditor} editor
 */
export const paste = (editor) => {
  navigator.clipboard
    .readText()
    .then((text) => {
      const selection = editor.getSelection();
      editor.executeEdits("paste", [{ range: selection, text }]);
    })
    .catch((editorError) => console.error(editorError));
};

/**
 * Gets editor selection and adds to clipboard.
 * @param {MonacoEditor} editor
 */
export const copy = (editor) => {
  copyToClipboard(editor);
};

/**
 * Gets editor selection, adds to clipboard, and removes selection from editor.
 * @param {MonacoEditor} editor
 */
export const cut = (editor) => {
  copyToClipboard(editor);
  const selection = editor.getSelection();
  editor.executeEdits("", [{ range: selection, text: null }]);
};

const CALLBACKS = {
  [C_KEY]: copy,
  [V_KEY]: paste,
  [X_KEY]: cut,
};

/**
 * Detects if keyset represents an action with a callback
 * @param {IKeyboardEvent} event
 * @returns {boolean}
 */
const isKeyset = (event) =>
  (event.metaKey || event.ctrlKey) && event.keyCode in CALLBACKS;

/**
 * Applies monaco fixes if needed.
 * @param {MonacoEditor} editor
 */
export const applyEditorFixes = (editor) => {
  NEEDS_FIXES &&
    editor.onKeyDown((event) => {
      if (isKeyset(event)) {
        consumeEvent(event);
        CALLBACKS[event.keyCode]?.(editor);
      }
    });
};

/**
 * Suppress Monaco's "Cannot find name" errors (TS2304/2552/2570) since users
 * can reference runtime globals the editor doesn't know about (GSAP, etc.)
 * @param {Monaco} monaco
 */
export const suppressGlobalJSUndefinedErrors = (monaco) => {
  const opts = { diagnosticCodesToIgnore: ERROR_SUPPRESSION_LIST };
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(opts);
  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(opts);
};
